/* eslint-disable react-hooks/exhaustive-deps */
import {
   Modal as AModal,
   Callout,
   IconButton,
   Typography,
} from '@airtel/air-design-system';
import { ButtonWidget, StatusCardWidget } from '@airtel/air-widgets-library';
import * as faceapi from 'face-api.js';
import { FC, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-native';
import faviconIcon from '../../assets/images/png/airtel.png';
import { generateInteractionId, generateUniqueId } from '../../common/commonUtils';
import { convertTimestampToDate } from '../../common/otpUtils';
import { prepareFormData } from '../../common/uploadUtils';
import {
   closeCameraModalWithErrorAction,
   ImageStore,
} from '../../common/userImageUploadUtils';
import { BASE_HREF, IS_LOCAL } from '../../constants/RouteConstants';
import FullScreenLoader from '../full-screen-loader';
import ModalCloseButton from '../modal-close-button';
import { ImageModalPropsTypes } from './ImageModalPropTypes';
import ImagePreview from './ImagePreview';
import { ChildComponentHandles, ImageWebcam } from './ImageWebcam';
import './imageModalStyles.scss';

const MODEL_URL: string = (IS_LOCAL ? '' : BASE_HREF) + '/models';

const ImageModal: FC<ImageModalPropsTypes> = (props) => {
   const {
      visible,
      artistRef,
      getReduxState,
      userLocation = {},
      isUploaded,
      showFaceMatchError,
      faceMathErrorText,
      setShowFaceMathError,
      setIsImageModalOpen,
   } = props;
   const { latitude = '', longitude = '' } = userLocation;

   const webcamRef = useRef<ChildComponentHandles>(null);
   const detectionInterval = useRef<ReturnType<typeof setTimeout> | null>(null);

   const [imageWithWaterMark, setImageWithWaterMark] = useState('');
   const [rawImage, setRawImage] = useState('');
   const [detectedFaceCount, setDetectedFaceCount] = useState<number | null>(null);
   const [modelsLoaded, setModelsLoaded] = useState<boolean>(false);
   const [stream, setStream] = useState<HTMLVideoElement | null>(null);
   const [hasPermission, setHasPermission] = useState(false);
   const [loader, setLoader] = useState(true);
   const [eyesCallout, setEyesCallout] = useState(false);
   const airtelIconRef = useRef<ImageBitmap | null>(null);

   useEffect(() => {
      // Handled: load modal will through error if video is not loaded
      if (visible && hasPermission) {
         loadFaceAPIModels();

         return () => {
            clearInterval(detectionInterval.current as ReturnType<typeof setTimeout>);
            faceapi.nets.tinyFaceDetector.dispose();
            setHasPermission(false);
         };
      }
   }, [visible, hasPermission]);

   useEffect(() => {
      if (modelsLoaded && stream) {
         detectFaces();
      }
   }, [modelsLoaded, eyesCallout])

   /**
    *  load face js models
    */
   useEffect(() => {
      if (webcamRef.current) {
         setStream(webcamRef.current?.getMediaStream?.() as HTMLVideoElement);
      }

      return () => {
         setStream(null);
      };
   }, [visible, stream, imageWithWaterMark]);

   useEffect(() => {
      const actions = [];
      const reduxState = getReduxState?.();
      const storeValues = reduxState?.storeValues;
      const isNewSKYCJourney = storeValues?.journeyContext?.journey === 'SKYC_NEW';
      if (detectedFaceCount === 0) {
         if (isNewSKYCJourney) {
            actions?.push({
               analyticsAction: 'click',
               analyticsData: {
                  eventCategory: 'NEW_ACQ_POSTPAID_SKYC',
                  section: 'stepsSection',
                  subSection: 'Step4Widget',
                  eventAction: 'impression',
                  eventLabel: 'retry',
                  eventValue: 'ensure that your face is clearly visible in the photo',
                  customValue: '',
                  customLabel: 'card',
                  prop35: '{{storeValues:orderData.orderData.cartId}}',
                  extraData12: '{{storeValues:orderData.orderData.cartItemId}}',
                  prop45: '{{storeValues:appData.subJourney}}',
                  eVar21: '{{storeValues:appData.loggedInNumber}}',
                  eVar36: '{{storeValues:appData.msisdn}}',
                  city: '{{storeValues:pincodeContext.pincodeDetails.city}}',
                  eVar25: '{{storeValues:pincodeContext.pincodeDetails.circleName}}',
                  prop4: 'SKYC',
                  extraData11: '{{storeValues:layoutContext.layout}}',
                  prop7: '{{storeValues:outstationContext.isOutstation}}',
                  prop9: '{{storeValues:appData.lob}}',
                  eVar10: 'error'
               },
            });
         } else {
            actions.push({
               analyticsAction: 'click',
               analyticsData: {
                  eventCategory: 'SKYC_LANDING_PAGE',
                  section: 'stepssection',
                  subSection: 'Step4Widget',
                  eventAction: 'error',
                  eventLabel: 'face not visible',
                  eventValue: 'Ensure that your face is clearly visible in the photo.',
                  eVar21: '{{storeValues:appData.msisdn}}',
                  extraData11: '{{storeValues:layoutContext.layout}}',
                  prop8: 'reverification',
                  prop6: '{{storeValues:siNumberStatusDetails.actionRequired}}',
                  prop7: '{{storeValues:siNumberStatusDetails.customerZone}}',
                  prop5: '{{storeValues:outstationContext.isOutstation}}',
               },
            });
         }
      } else if (detectedFaceCount !== 1) {
         actions.push({
            analyticsAction: 'click',
            analyticsData: {
               eventCategory: 'SKYC_LANDING_PAGE',
               section: 'stepssection',
               subSection: 'Step4Widget',
               eventAction: 'error',
               eventLabel: 'Multiple faces detected.',
               eventValue: 'Multiple faces detected.',
               eVar21: '{{storeValues:appData.msisdn}}',
               extraData11: '{{storeValues:layoutContext.layout}}',
               prop8: 'reverification',
               prop6: '{{storeValues:siNumberStatusDetails.actionRequired}}',
               prop7: '{{storeValues:siNumberStatusDetails.customerZone}}',
               prop5: '{{storeValues:outstationContext.isOutstation}}',
            },
         });
      }

      if(eyesCallout){
         if (isNewSKYCJourney) {
            actions?.push({
               analyticsAction: 'click',
               analyticsData: {
                  eventCategory: 'NEW_ACQ_POSTPAID_SKYC',
                  section: 'stepsSection',
                  subSection: 'Step4Widget',
                  eventAction: 'impression',
                  eventLabel: 'retry',
                  eventValue: 'ensure that your eyes are open in the photo',
                  customValue: '',
                  customLabel: 'card',
                  prop35: '{{storeValues:orderData.orderData.cartId}}',
                  extraData12: '{{storeValues:orderData.orderData.cartItemId}}',
                  prop45: '{{storeValues:appData.subJourney}}',
                  eVar21: '{{storeValues:appData.loggedInNumber}}',
                  eVar36: '{{storeValues:appData.msisdn}}',
                  city: '{{storeValues:pincodeContext.pincodeDetails.city}}',
                  eVar25: '{{storeValues:pincodeContext.pincodeDetails.circleName}}',
                  prop4: 'SKYC',
                  extraData11: '{{storeValues:layoutContext.layout}}',
                  prop7: '{{storeValues:outstationContext.isOutstation}}',
                  prop9: '{{storeValues:appData.lob}}',
                  eVar10: 'error'
               },
            });
         } 
      }

      if (actions.length) artistRef?.current?.onUserAction({ actions });
   }, [detectedFaceCount]);

   const loadFaceAPIModels = async () => {
      try {
         await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
         await faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL);
         await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
         await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
         setModelsLoaded(true);
      } catch (err) {
         setModelsLoaded(false);
      }
   };

   const detectFaces = async () => {
      clearInterval(detectionInterval.current as ReturnType<typeof setTimeout>);

      detectionInterval.current = setInterval(async () => {
         if (webcamRef && stream) {
            const faces = await faceapi.detectAllFaces(
               stream,
               new faceapi.TinyFaceDetectorOptions()
            );
            const detections = await faceapi.detectSingleFace(
               stream
            ).withFaceLandmarks();
            getEyeDetection(detections)
            setDetectedFaceCount(faces.length);
         }
      }, 250);
   };

   const calculateEAR = (eye: any) => {
      const p2_p6 = Math.sqrt(Math.pow(eye[1].x - eye[5].x, 2) + Math.pow(eye[1].y - eye[5].y, 2));
      const p3_p5 = Math.sqrt(Math.pow(eye[2].x - eye[4].x, 2) + Math.pow(eye[2].y - eye[4].y, 2));
      const p1_p4 = Math.sqrt(Math.pow(eye[0].x - eye[3].x, 2) + Math.pow(eye[0].y - eye[3].y, 2));
      const ear = (p2_p6 + p3_p5) / (2.0 * p1_p4);
      return ear;
   }

   const getEyeDetection = (detections: any) => {
      // console.log("detections::::", detections);
      if (detections == null) {
         setEyesCallout(false);
         return;
      }
      const leftEye = detections.landmarks.getLeftEye();
      const rightEye = detections.landmarks.getRightEye();
      const leftEAR = calculateEAR(leftEye);
      const rightEAR = calculateEAR(rightEye);
      const EAR_THRESHOLD = 0.3;
      const leftEyeClosed = leftEAR < EAR_THRESHOLD;
      const rightEyeClosed = rightEAR < EAR_THRESHOLD;

      if (leftEyeClosed && rightEyeClosed) {
         // console.log("Eyes are closed");
         setEyesCallout(true);
      } else {
         //   console.log("Eyes are open");
         setEyesCallout(false);
      }
   }

   const handleVideoPlay = () => {
      if (modelsLoaded && stream) {
         detectFaces();
      }
   };

   const capturePhoto = async () => {
      setLoader(true);
      const imageSrc = webcamRef.current?.getScreenshot?.();
      if (imageSrc) {
         if (isUploaded) isUploaded.current = false;
         clearInterval(detectionInterval.current as ReturnType<typeof setTimeout>);
         setRawImage(await getProcessedImage(imageSrc));
         setImageWithWaterMark(await getProcessedImage(imageSrc, true));
      }
      setLoader(false);
      const actions = [];

      actions.push({
         analyticsAction: 'click',
         analyticsData: {
            eventCategory: 'SKYC_LANDING_PAGE',
            section: 'stepssection',
            subSection: 'Step4Widget',
            eventAction: 'click',
            eventLabel: 'capture picture',
            eVar21: '{{storeValues:appData.msisdn}}',
            extraData11: '{{storeValues:layoutContext.layout}}',
            prop8: 'reverification',
            prop6: '{{storeValues:siNumberStatusDetails.actionRequired}}',
            prop7: '{{storeValues:siNumberStatusDetails.customerZone}}',
            prop5: '{{storeValues:outstationContext.isOutstation}}',
         },
      });

      artistRef?.current?.onUserAction({
         actions,
      });
   };

   const onPressRetry = () => {
      setLoader(true);
      setImageWithWaterMark('');

      const actions = [];
      const reduxState = getReduxState?.();
      const storeValues = reduxState?.storeValues;
      const isNewSKYCJourney = storeValues?.journeyContext?.journey === 'SKYC_NEW';

      if (isNewSKYCJourney) {
         actions?.push({
            analyticsAction: 'click',
            analyticsData: {
               eventCategory: 'NEW_ACQ_POSTPAID_SKYC',
               section: 'stepsSection',
               subSection: 'Step4Widget',
               eventAction: 'click',
               eventLabel: 'retry',
               eventValue: '',
               customValue: '',
               customLabel: 'button',
               prop35: '{{storeValues:orderData.orderData.cartId}}',
               extraData12: '{{storeValues:orderData.orderData.cartItemId}}',
               prop45: '{{storeValues:appData.subJourney}}',
               eVar21: '{{storeValues:appData.loggedInNumber}}',
               eVar36: '{{storeValues:appData.msisdn}}',
               city: '{{storeValues:pincodeContext.pincodeDetails.city}}',
               eVar25: '{{storeValues:pincodeContext.pincodeDetails.circleName}}',
               prop4: 'SKYC',
               extraData11: '{{storeValues:layoutContext.layout}}',
               prop7: '{{storeValues:outstationContext.isOutstation}}',
               prop9: '{{storeValues:appData.lob}}'
            },
         });
      } else {
         actions.push({
            analyticsAction: 'click',
            analyticsData: {
               eventCategory: 'SKYC_LANDING_PAGE',
               section: 'stepssection',
               subSection: 'Step4Widget',
               eventAction: 'click',
               eventLabel: 'retry picture',
               eVar21: '{{storeValues:appData.msisdn}}',
               extraData11: '{{storeValues:layoutContext.layout}}',
               prop8: 'reverification',
               prop6: '{{storeValues:siNumberStatusDetails.actionRequired}}',
               prop7: '{{storeValues:siNumberStatusDetails.customerZone}}',
               prop5: '{{storeValues:outstationContext.isOutstation}}',
            },
         });
      }
      artistRef?.current?.onUserAction({
         actions,
      });
   };

   const onPressSubmit = async () => {
      if (!imageWithWaterMark) return;

      const reduxState = getReduxState?.();
      const storeValues = reduxState?.storeValues;
      const isNewSKYCJourney = storeValues?.journeyContext?.journey === 'SKYC_NEW';
      const circle = storeValues?.entityDetails?.circleId || '102';
      const actions = [];
      const journeyType = storeValues?.appData?.lob;
      let headers = {};

      let imageWithWaterMarkData: any = {
         imageValue: imageWithWaterMark,
         interactionId: generateInteractionId(),
         latitude,
         longitude,
         lob: 'CUSTOMER_REVERIFICATION',
         imageType: 'USER_IMAGE',
         documentType: 'USER_IMAGE',
         source: 'SHOP_RV',
      };

      let rawImageData: any = {
         imageValue: rawImage,
         interactionId: generateInteractionId(),
         latitude,
         longitude,
         lob: 'CUSTOMER_REVERIFICATION',
         imageType: 'UNCROPPED_USER_IMAGE',
         documentType: 'UNCROPPED_USER_IMAGE',
         imageSameAsPoa: 'false',
         matchFaceWithImage: 'USER_UIDAI_IMAGE',
         source: 'SHOP_RV',
      };
      let imageKey = 'imageValue';

      if (isNewSKYCJourney) {
         imageKey = 'docFile';
         const kycId = storeValues?.skycContext?.kycId;

         headers = {
            source: 'shop',
            'x-correlation-id': generateUniqueId(),
         };

         rawImageData = {
            docFile: rawImage,
            docType: 'UNCROPPED_USER_IMAGE',
            kycId,
            kycType: 'SKYC',
            channel: 'SHOP',
            circle,
            latitude,
            longitude,
            journeyType,
            targetImageMatch: JSON.stringify([
               { targetImage: 'USER_UIDAI_IMAGE', correlationId: kycId },
            ]),
            requestTime: new Date().toISOString(),
         };

         imageWithWaterMarkData = {
            docFile: imageWithWaterMark,
            docType: 'USER_IMAGE',
            kycId,
            kycType: 'SKYC',
            channel: 'SHOP',
            circle,
            latitude,
            longitude,
            journeyType,
            requestTime: new Date().toISOString(),
         };
      }
      const rawImageFormData = await prepareFormData(rawImageData, false, imageKey);
      const imageWithWaterMarkFormData = await prepareFormData(
         imageWithWaterMarkData,
         false,
         imageKey
      );

      const imageStore = ImageStore.getInstance();
      imageStore.setItem('waterMarkUserImage', imageWithWaterMarkFormData);

      if (isUploaded && isUploaded.current) {
         actions.push({
            payload: {
               args: ['closeWithoutUpload'],
               utilName: 'closeImageModal',
            },
            type: 'callUtilFunction',
         });
      } else {
         let apiUrl = 'skyc/ecaf-commons/api/v2/image/upload/facematch/v1';

         if (isNewSKYCJourney) {
            apiUrl = 'new-acq/dochub/doc-hub/upload/image';
         }

         actions.push(
            {
               type: 'fullPageLoader',
               payload: {
                  show: true,
               },
            },
            {
               type: 'apiCall',
               payload: {
                  requestType: 'POST',
                  apiUrl,
                  authTokenGroup: 'SKYC_REVERIFICATION',
                  ...{ headers },
                  body: rawImageFormData,
                  onSuccess: {
                     actions: [
                        {
                           payload: {
                              args: [],
                              utilName: 'handleFaceMatchResponse',
                           },
                           type: 'callUtilFunction',
                        },
                        {
                           analyticsAction: 'click',
                           analyticsData: {
                              eventCategory: 'SKYC_LANDING_PAGE',
                              section: 'stepssection',
                              subSection: 'step4Widget',
                              eventAction: 'pageclose',
                              eVar21: '{{storeValues:appData.msisdn}}',
                              extraData11: '{{storeValues:layoutContext.layout}}',
                              prop8: 'reverification',
                              prop7: '{{storeValues:siNumberStatusDetails.customerZone}}',
                              prop6: '{{storeValues:siNumberStatusDetails.actionRequired}}',
                              prop5: '{{storeValues:outstationContext.isOutstation}}',
                           },
                        },
                     ],
                  },
                  onError: {
                     actions: [
                        {
                           type: 'fullPageLoader',
                           payload: {
                              show: false,
                           },
                        },
                        {
                           type: 'showToast',
                           payload: {
                              title: 'something went wrong please try again',
                              dismissDuration: 5000,
                              position: 'bottom-center',
                              state: 'error',
                              style: {
                                 zIndex: 10000,
                              },
                           },
                        },
                        ...(isNewSKYCJourney ? [
                           {
                              analyticsAction: 'impression',
                              analyticsData: {
                                 eventCategory: 'NEW_ACQ_POSTPAID_SKYC',
                                 section: 'stepsSection',
                                 subSection: 'Step4Widget',
                                 eventAction: 'impression',
                                 eventLabel: 'retry',
                                 eventValue: 'ecaf',
                                 customValue: 'image upload failed',
                                 customLabel: 'card',
                                 prop35: '{{storeValues:orderData.orderData.cartId}}',
                                 extraData12: '{{storeValues:orderData.orderData.cartItemId}}',
                                 prop45: '{{storeValues:appData.subJourney}}',
                                 eVar21: '{{storeValues:appData.loggedInNumber}}',
                                 eVar36: '{{storeValues:appData.msisdn}}',
                                 city: '{{storeValues:pincodeContext.pincodeDetails.city}}',
                                 eVar25: '{{storeValues:pincodeContext.pincodeDetails.circleName}}',
                                 prop4: 'SKYC',
                                 extraData11: '{{storeValues:layoutContext.layout}}',
                                 prop7: '{{storeValues:outstationContext.isOutstation}}',
                                 prop9: '{{storeValues:appData.lob}}',
                                 eVar10: 'error'
                              },
                           }
                        ] : [])
                     ],
                  },
               },
            }
         );
      }
      if (isNewSKYCJourney) {
         actions?.push({
            analyticsAction: 'click',
            analyticsData: {
               eventCategory: 'NEW_ACQ_POSTPAID_SKYC',
               section: 'stepsSection',
               subSection: 'Step4Widget',
               eventAction: 'click',
               eventLabel: 'submit',
               eventValue: '',
               customValue: '',
               customLabel: 'button',
               prop35: '{{storeValues:orderData.orderData.cartId}}',
               extraData12: '{{storeValues:orderData.orderData.cartItemId}}',
               prop45: '{{storeValues:appData.subJourney}}',
               eVar21: '{{storeValues:appData.loggedInNumber}}',
               eVar36: '{{storeValues:appData.msisdn}}',
               city: '{{storeValues:pincodeContext.pincodeDetails.city}}',
               eVar25: '{{storeValues:pincodeContext.pincodeDetails.circleName}}',
               prop4: 'SKYC',
               extraData11: '{{storeValues:layoutContext.layout}}',
               prop7: '{{storeValues:outstationContext.isOutstation}}',
               prop9: '{{storeValues:appData.lob}}'
            },
         });
      } else {
         actions.push({
            analyticsAction: 'click',
            analyticsData: {
               eventCategory: 'SKYC_LANDING_PAGE',
               section: 'stepssection',
               subSection: 'Step4Widget',
               eventAction: 'click',
               eventLabel: 'submit picture',
               eVar21: '{{storeValues:appData.msisdn}}',
               extraData11: '{{storeValues:layoutContext.layout}}',
               prop8: 'reverification',
               prop6: '{{storeValues:siNumberStatusDetails.actionRequired}}',
               prop7: '{{storeValues:siNumberStatusDetails.customerZone}}',
               prop5: '{{storeValues:outstationContext.isOutstation}}',
            },
         });
      }
      artistRef?.current?.onUserAction({
         actions,
      });
   };

   const getLatLongString = () => {
      return !!latitude && !!longitude
         ? `${parseFloat(latitude).toFixed(3)}, ${parseFloat(longitude).toFixed(3)} `
         : '';
   };

   const getProcessedImage = (imageSrc: string, withWatermark = false) => {
      return new Promise<string>((resolve) => {
         const reduxState = getReduxState?.();
         const canvas = document.createElement('canvas');
         const context = canvas?.getContext('2d');

         const imageObj = new Image();
         imageObj.src = imageSrc as string;

         imageObj.onload = async function () {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight - 84;

            if (context && canvas.width && canvas.height && airtelIconRef.current) {
               // Clear the canvas
               context.clearRect(0, 0, canvas.width, canvas.height);
               context.drawImage(
                  imageObj,
                  imageObj.width / 2 - window.innerWidth / 2,
                  0,
                  window.innerWidth,
                  window.innerHeight,
                  0,
                  0,
                  window.innerWidth,
                  window.innerHeight
               );

               if (withWatermark) {
                  // Add inset bottom shadow
                  context.fillStyle = 'rgba(0, 0, 0, 1)';
                  context.shadowColor = 'rgba(0, 0, 0, 1)';
                  context.shadowBlur = 150;
                  context.shadowOffsetX = -100;
                  context.shadowOffsetY = -60;
                  context.fillRect(0, canvas.height, canvas.width + 200, 80);

                  context.fillStyle = 'white';
                  context.font = 'regular 25px Arial';

                  const latLong = getLatLongString();
                  const isNewSKYCJourney =
                     reduxState?.storeValues?.journeyContext?.journey === 'SKYC_NEW';
                  const { siNumberStatusDetails } = reduxState?.storeValues ?? {};
                  let caFNumber = siNumberStatusDetails?.cafNumber;

                  if (isNewSKYCJourney) {
                     caFNumber = reduxState?.storeValues?.skycContext?.cafNumber;
                  }

                  let bottomMargin = 20;

                  context.fillText(
                     convertTimestampToDate(new Date().getTime(), "IMAGE_MODAL"),
                     10,
                     canvas.height - bottomMargin
                  );

                  if (latLong) {
                     bottomMargin += 15;
                     context.fillText(latLong, 10, canvas.height - bottomMargin);
                  }

                  if (caFNumber) {
                     bottomMargin += 15;
                     context.fillText(caFNumber, 10, canvas.height - bottomMargin);
                  }


                  bottomMargin += 35;
                  context.translate(10, canvas.height - bottomMargin);
                  context.drawImage(airtelIconRef.current, 0, 0, 25, 25);
               }

               context.fill();
               resolve(canvas.toDataURL('image/jpeg'));
            }
         };
      });
   };

   const handleFaceMatchModalClose = () => {
      setShowFaceMathError?.(false);
      onPressRetry();
   };

   const closeImageModalWithError = () => {
      closeCameraModalWithErrorAction('image', artistRef);
   };

   useEffect(() => {
      (async function () {
         const result = await fetch(faviconIcon);
         const blob = await result.blob();
         airtelIconRef.current = await createImageBitmap(blob)
      })();
   },[])

   return (
      <Modal visible={visible} animationType="slide" transparent={false}>
         <ModalCloseButton onPress={setIsImageModalOpen} />
         <AModal
            initialVisible={showFaceMatchError}
            swipeEnabled={false}
            onCloseHandler={handleFaceMatchModalClose}
            showCross
         >
            <StatusCardWidget
               title={{
                  text: 'Error during photo validation',
               }}
               subtitle={{
                  text: faceMathErrorText,
               }}
               statusCardImage={{
                  imageUrl:
                     'https://assets.airtel.in/static-assets/acq-skyc/aadhaar-photo-mis-match.png',
               }}
               type="image"
               primaryCtaTitle="Retry"
               primaryButtonType="primary"
               primaryButtonVersion="variable-width"
               ctaDirection="column"
               buttonWidgetProps={{
                  leftCta: {
                     onPress: handleFaceMatchModalClose,
                  },
               }}
            />
         </AModal>
         <div className="outer-container">
            <div
               className="image-container">
               {imageWithWaterMark ? (
                  <ImagePreview src={imageWithWaterMark} />
               ) : (
                  <>
                     {loader && <FullScreenLoader />}

                     <ImageWebcam
                        audio={false}
                        ref={webcamRef}
                        onPlay={handleVideoPlay}
                        closeImageModalWithError={closeImageModalWithError}
                        setHasPermission={setHasPermission}
                        onLoadStart={() => {
                           setLoader(true);
                        }}
                        onCanPlay={() => {
                           setLoader(false);
                        }}
                     />

                     {!loader && (
                        <div className="focus-overlay">
                           {detectedFaceCount !== null && detectedFaceCount !== 1 && (
                              <div className="img-error-callout">
                                 <Callout
                                    description={
                                       eyesCallout ? 'Ensure that your eyes are open in the photo.' :
                                          detectedFaceCount === 0
                                             ? 'Ensure that your face is clearly visible in the photo.'
                                             : 'Multiple faces detected.'
                                    }
                                    iconName="AlertMediumIcon"
                                    state="error"
                                    version="variable-width"
                                 />
                              </div>
                           )}
                           {detectedFaceCount === 1 && eyesCallout && (
                              <div className="img-error-callout">
                                 <Callout
                                    description={'Ensure that your eyes are open in the photo.'}
                                    iconName="AlertMediumIcon"
                                    state="error"
                                    version="variable-width"
                                 />
                              </div>
                           )}
                        </div>
                     )}
                  </>
               )}
            </div>
            <div className="btn-container">
               {imageWithWaterMark ? (
                  <ButtonWidget
                     direction="row"
                     leftCta={{
                        size: 'md',
                        buttonType: 'secondary',
                        title: 'Retry',
                        onPress: onPressRetry,
                     }}
                     rightCta={{
                        size: 'md',
                        buttonType: 'primary',
                        title: 'Submit',
                        onPress: onPressSubmit,
                     }}
                     style={{
                        width: '100%',
                     }}
                  />
               ) : (
                  <>
                     <Typography
                        type="body-para-md-bold"
                        text="Position your face in the center"
                        style={{
                           marginBottom: 16,
                        }}
                     />
                     <IconButton
                        iconName="CameraOnFilledIcon"
                        onPress={capturePhoto}
                        size="lg"
                        theme="light"
                        type="primary"
                        variant="icon"
                        disabled={loader}
                     />
                  </>
               )}
            </div>
         </div>
      </Modal>
   );
};

export default ImageModal;
